/**
 * OAuth consent for the Flux Agent editor MCP server. An AI assistant's sign-in lands here after
 * the Worker validated the request; the signed-in editor approves or denies, and the browser is
 * sent back to the assistant. The Worker decides eligibility and builds the return URL.
 *
 * @module apps/web/src/pages/EditorAuthorizePage
 */

import { useEffect, useState } from 'react';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { editorToolsApi, type EditorAuthorization } from '@/lib/editorToolsApi';

/** Consent page for one pending sign-in request named by the `request` query parameter. */
export function EditorAuthorizePage() {
  const requestId = new URLSearchParams(window.location.search).get('request') ?? '';
  const [details, setDetails] = useState<EditorAuthorization | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!requestId) {
      setError('This page needs a sign-in request. Start again from your AI assistant.');
      return;
    }
    void editorToolsApi.authorization(requestId).then(result => {
      if (result.success && result.data) setDetails(result.data);
      else setError(result.error?.message || 'This sign-in request could not be loaded.');
    });
  }, [requestId]);

  /** Approve or deny, then hand the browser back to the assistant. */
  const decide = async (approve: boolean) => {
    setBusy(true);
    const result = approve
      ? await editorToolsApi.approve(requestId)
      : await editorToolsApi.deny(requestId);
    if (result.success && result.data?.redirectTo) {
      window.location.assign(result.data.redirectTo);
      return;
    }
    setBusy(false);
    setError(result.error?.message || 'That did not work. Start again from your AI assistant.');
  };

  const closed = details && (details.status !== 'pending' || details.expired);
  return (
    <div className="mx-auto max-w-lg p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {details?.editor ? (
              <ShieldCheck className="h-5 w-5" />
            ) : (
              <ShieldAlert className="h-5 w-5" />
            )}
            Connect an AI assistant
          </CardTitle>
          <CardDescription>Flux Agent editor access</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {error && <p className="text-destructive">{error}</p>}
          {!error && !details && <p className="text-muted-foreground">Loading…</p>}
          {details && (
            <>
              <p>
                <strong>{details.clientName}</strong> wants to read the Flux Agent system prompt and
                playbooks and propose and apply changes to them as <strong>{details.email}</strong>.
              </p>
              <p className="text-muted-foreground">
                The connection code will be sent to{' '}
                <span className="font-mono">{details.redirectHost ?? details.redirectUri}</span>.
                Only approve if you just started this from your own assistant.
              </p>
              {closed && (
                <p className="text-destructive">
                  This request was already used or has expired. Start again from your AI assistant.
                </p>
              )}
              {!details.editor && !closed && (
                <p className="text-destructive">
                  Your account is not an authorised Flux Agent editor, so it cannot approve this.
                </p>
              )}
              <div className="flex gap-3">
                <Button
                  type="button"
                  disabled={busy || Boolean(closed) || !details.editor}
                  onClick={() => decide(true)}
                >
                  Approve
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy || Boolean(closed)}
                  onClick={() => decide(false)}
                >
                  Deny
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
